import { sharedSchema } from "@shared-database/pgSchema";
import { File } from "@shared-database/models/file";
import { KnowledgeEntity } from "@shared-database/models/rag/knowledge-entity";
import { KnowledgeGraph } from "@shared-database/models/rag/knowledge-graph";
import {
  leoID,
  leoNotNullForeignID,
  timestampColumns,
} from "@shared-database/utils";
import type { InferInsertModel, InferSelectModel } from "drizzle-orm";
import { index, integer, text, uniqueIndex } from "drizzle-orm/pg-core";

/**
 * Provenance: which files contributed to each graph-scoped entity.
 * Used as the privacy gate at query time: an entity is only surfaced if the
 * querying user has access to at least one of its source files. The
 * perFileDescription snippets are concatenated into a user-visible
 * description built from only accessible sources.
 */
export const KnowledgeEntitySource = sharedSchema.table(
  "KnowledgeEntitySource",
  {
    // Mixins
    ...timestampColumns,

    // Properties
    id: leoID("id"),
    knowledgeGraphId: leoNotNullForeignID("knowledgeGraphId").references(
      () => KnowledgeGraph.id,
      { onDelete: "cascade" },
    ),
    perFileDescription: text("perFileDescription").notNull(),
    mentionCount: integer("mentionCount").notNull().default(1),

    // References
    entityId: leoNotNullForeignID("entityId").references(
      () => KnowledgeEntity.id,
      { onDelete: "cascade" },
    ),
    fileId: leoNotNullForeignID("fileId").references(() => File.id, {
      onDelete: "cascade",
    }),
  },
  (table) => [
    uniqueIndex("KnowledgeEntitySource_uniqueIdx").on(
      table.entityId,
      table.fileId,
    ),
    index("KnowledgeEntitySource_entityIdx").on(table.entityId),
    index("KnowledgeEntitySource_fileIdx").on(table.fileId),
    index("KnowledgeEntitySource_knowledgeGraphIdx").on(table.knowledgeGraphId),
    index("KnowledgeEntitySource_knowledgeGraphFileEntityIdx").on(
      table.knowledgeGraphId,
      table.fileId,
      table.entityId,
    ),
  ],
);

// Types
export type KnowledgeEntitySourceType = InferSelectModel<
  typeof KnowledgeEntitySource
>;
export type KnowledgeEntitySourceInsertType = InferInsertModel<
  typeof KnowledgeEntitySource
>;
